/**
 * "typeAnswer": the player types the answer (PlayspaceTypeAnswer.cs/.prefab).
 * Correct when it matches any accepted answer, case-insensitively.
 *
 * Layout (prefab › Content, a bottom-aligned column over the whole screen):
 * the blue InputField 1024×200 sits 250 px above the bottom (room for the
 * clock), the green Submit button 400×120 appears 50 px above it as soon as
 * something is typed, and after a wrong answer the green CorrectAnswer panel
 * 1400×310 shows the first accepted answer in the middle of the screen.
 */
import { useState, type FormEvent } from "react";
import type { SlideOf } from "@makeit/schema";
import { libraryUrl } from "../../data/media";
import { ElementImage } from "../elements";
import { QuestionView } from "./QuizSlide";
import { COLOR_GREEN, COLOR_RED, useActivity } from "../activity";
import { t } from "../i18n";

const ui = (n: string) => libraryUrl(`ui/${n}.png`);

export function TypeAnswerSlide({ slide }: { slide: SlideOf<"typeAnswer"> }) {
  const activity = useActivity(1);
  const [value, setValue] = useState("");
  const [result, setResult] = useState<"correct" | "wrong" | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (result) return;
    const typed = value.trim();
    if (!typed) return;
    const ok = slide.answers.some((a) => a.text.content.trim().toLowerCase() === typed.toLowerCase());
    setResult(ok ? "correct" : "wrong");
    if (ok) activity.correct(typed);
    else activity.wrong(false, typed);
  };

  const hasImage = !!slide.image?.image?.fileName;
  const bgButton = { WebkitMaskImage: `url(${ui("bgButton")})`, maskImage: `url(${ui("bgButton")})` };
  return (
    <div className={`quiz type-answer ${activity.quizMode ? "quiz-project" : ""} ${hasImage ? "has-image" : ""}`}>
      {hasImage && slide.image && (
        <div className="quiz-image">
          <ElementImage element={slide.image} />
        </div>
      )}
      <div className="quiz-question">
        <QuestionView element={slide.question} />
      </div>
      <form className="type-answer-form" onSubmit={submit}>
        {!result && value.trim() && (
          <button className="type-answer-submit" type="submit" style={bgButton}>
            {t("submit")}
          </button>
        )}
        <div className="type-answer-field" style={result ? { background: result === "correct" ? COLOR_GREEN : COLOR_RED } : undefined}>
          <input
            className="type-answer-input"
            dir="auto"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            readOnly={!!result}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            enterKeyHint="done"
            aria-label={t("submit")}
          />
          {result && <img className={`type-answer-mark ${result}`} src={ui(result === "correct" ? "correctMark" : "wrongMark")} alt="" draggable={false} />}
        </div>
      </form>
      {result === "wrong" && (
        <div className="type-answer-solution">
          <span className="type-answer-solution-title">{t("correctAnswerWas")}</span>
          <div className="type-answer-solution-panel" style={bgButton}>
            <span dir="auto">{slide.answers[0]?.text.content}</span>
          </div>
        </div>
      )}
    </div>
  );
}
